// src/pages/ChatDocuments/chatAsync.ts
import {
  loadMySecretKey,
  getPublicKeyForUser,
  publicKeyFromSecret,
  unwrapMediaKey,
  decryptMediaFromUrlToObjectURL,
  idDownload,
  encryptMessage,
  getMySecretFromServer,
  saveMySecretKey,
} from "../crypto/simpleE2EE";
import nacl from "tweetnacl";

export type MediaLike = {
  _id?: string;
  media?: string;
  mediaIv?: string;
  mediaMime?: string;
  mediaKeyEnvelope?: any;
  mediaKeySelfEnvelope?: any;
  // mutated/filled by the helpers
  decryptedMediaUrl?: string;
};

export function b64urlToBytes(s: string): Uint8Array {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const base64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");

  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) {
    out[i] = raw.charCodeAt(i);
  }
  return out;
}

// exact ArrayBuffer view of a Uint8Array (Safari-friendly)
const toExactArrayBuffer = (u8: Uint8Array): ArrayBuffer => {
  const ab = new ArrayBuffer(u8.byteLength);
  new Uint8Array(ab).set(u8);
  return ab;
};

export async function decryptAesGcmToBlob(
  encBuf: ArrayBuffer,
  key: CryptoKey,
  iv: Uint8Array,
  mime = "application/octet-stream"
): Promise<Blob> {
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toExactArrayBuffer(iv) },
    key,
    encBuf
  );
  return new Blob([plain], { type: mime });
}

// Unique id per logical "message group" for notifications bundling
const makeBundleKey = () =>
  (crypto as any).randomUUID?.() ||
  `bundle-${Date.now()}-${Math.random().toString(36).slice(2)}`;


// --- add near the top of chatAsync.ts (local helpers) ---
const asEnv = (x: any) => {
  if (!x) return null;
  if (typeof x === "object") return x;
  if (typeof x === "string") { try { return JSON.parse(x); } catch {} }
  return null;
};
const idOfAny = (u: any) =>
  typeof u === "string" ? u : (u?._id || u?.id || "");

// --- replace your existing decryptMedia with this version ---
export async function decryptMedia<
  T extends MediaLike & { sender?: any; recipient?: any }
>(
  m: T,
  token?: string | null,
  myUserId?: string
) {
  if (m.decryptedMediaUrl && m.decryptedMediaUrl !== "__FAIL__") return;
  if (!m.mediaIv) return;

  // decide envelope by who SENT the message
  const me = myUserId || "";
  const iAmSender = me && idOfAny(m.sender) && String(idOfAny(m.sender)) === String(me);

  const envRaw = iAmSender ? m.mediaKeySelfEnvelope : m.mediaKeyEnvelope;
  const env = asEnv(envRaw);
if (!env) return;

try {
  const mySecret = await requireMySecret();
  const keyB64u = unwrapMediaKey(env, mySecret);

  const url = m._id ? idDownload(String(m._id), "media") : (m.media as string);
  m.decryptedMediaUrl = await decryptMediaFromUrlToObjectURL(
    url,
    m.mediaIv!,
    keyB64u,
    token,
    m.mediaMime || "application/octet-stream"
  );
} catch (e) {
  console.warn("media decrypt failed", e);
  m.decryptedMediaUrl = "__FAIL__";
}

}

/* ----------------------- small local helpers ----------------------- */
const toB64Url = (u8: Uint8Array) => {
  const b64 = btoa(String.fromCharCode(...u8));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const fileToArrayBuffer = (file: File) =>
  new Promise<ArrayBuffer>((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error);
    fr.onload = () => resolve(fr.result as ArrayBuffer);
    fr.readAsArrayBuffer(file);
  });

  export const requireMySecret = async (): Promise<string> => {
    // 1) try localStorage
    const local = loadMySecretKey();
    if (local) return local;
  
    // 2) try server backup (chatSecretKey)
    const fromServer = await getMySecretFromServer();
    if (fromServer) {
      // cache it for next time
      saveMySecretKey(fromServer);
      return fromServer;
    }
  
    // 3) still nothing → hard fail
    throw new Error("Missing E2EE secret key on this device.");
  };
  

  export async function requireRecipientKey(
    userId: string,
    myId?: string
  ): Promise<string> {
    // try once
    let k = await getPublicKeyForUser(userId);
    if (typeof k === "string" && k.length >= 40) return k;
  
    // self-chat fallback: derive pub from my own secret
    if (userId && myId && userId === myId) {
      const sec = await requireMySecret();      // ← FIXED
      return publicKeyFromSecret(sec);
    }
  
    // brief retry (recipient may have just published)
    await new Promise((r) => setTimeout(r, 400));
    k = await getPublicKeyForUser(userId);
    if (typeof k === "string" && k.length >= 40) return k;
  
    throw new Error("Recipient hasn’t published a chat key yet. Ask them to open chat once.");
  }
  

  export async function aesGcmEncryptFile(
    file: File
  ): Promise<{ cipherBlob: Blob; ivB64u: string; keyB64u: string }> {
    // XSalsa20-Poly1305 (NaCl secretbox), despite the old name
    const plainBuf = await fileToArrayBuffer(file);
    const plain = new Uint8Array(plainBuf);
  
    const keyBytes = nacl.randomBytes(32); // 32-byte key
    const iv = nacl.randomBytes(24);       // 24-byte nonce
  
    const ct = nacl.secretbox(plain, iv, keyBytes);
  
    return {
      cipherBlob: new Blob([toExactArrayBuffer(ct)], { type: "application/octet-stream" }),
      ivB64u: toB64Url(iv),
      keyB64u: toB64Url(keyBytes),
    };    
  }
  
export async function objectUrlToBlob(url: string): Promise<Blob> {
  // Handle data: URLs explicitly
  if (url.startsWith("data:")) {
    const [meta, b64] = url.split(",");
    const mime = meta.split(":")[1]?.split(";")[0] || "application/octet-stream";
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }

  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.blob();
  } catch {
    // Safari fallback: XHR is more reliable for blob: URLs
    return await new Promise<Blob>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url);
      xhr.responseType = "blob";
      xhr.onload = () => resolve(xhr.response);
      xhr.onerror = () => reject(new Error("XHR blob load failed"));
      xhr.send();
    });
  }
}

export async function aesGcmEncryptBlob(
  blob: Blob
): Promise<{ cipherBlob: Blob; ivB64u: string; keyB64u: string }> {
  // XSalsa20-Poly1305 (NaCl secretbox) for arbitrary blobs
  const plainBuf = await blob.arrayBuffer();
  const plain = new Uint8Array(plainBuf);

  const keyBytes = nacl.randomBytes(32);
  const iv = nacl.randomBytes(24);

  const ct = nacl.secretbox(plain, iv, keyBytes);

  return {
    cipherBlob: new Blob([toExactArrayBuffer(ct)], { type: "application/octet-stream" }),
    ivB64u: toB64Url(iv),
    keyB64u: toB64Url(keyBytes),
  };
  
}


/** Optional helper (keep only if you still use it somewhere else) */
export async function toObjectUrlWithAuth(url: string, token?: string | null): Promise<string> {
  const toAbsolute = (u: string) => {
    try {
      new URL(u);
      return u;
    } catch {}
    const base = "http://localhost:3001";
    return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
  };

  const absUrl = toAbsolute(url);
  const t = token ?? undefined;

  let headers: Record<string, string> | undefined;
  try {
    const u = new URL(absUrl);
    // Don’t send Authorization to public CDNs
    if (!/(amazonaws\.com|cloudfront\.net)$/i.test(u.hostname) && t) {
      headers = { Authorization: `Bearer ${t}` };
    }
  } catch {}

  const res = await fetch(absUrl, { headers });
  if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Upload a single media file with TTL (clear or encrypted)
export async function sendOneMedia({
  file,
  toUserId,
  ttlAfterViewSec,
  replyToId,
  encryptedMedia,
  api,                // <-- pass axios instance in
  token,              // <-- pass bearer (optional if your api adds it automatically)
  bundleKey,
}: {
  file: File;
  toUserId: string;
  ttlAfterViewSec: number;
  replyToId?: string;
  encryptedMedia?: {
    mediaIv: string;
    mediaKeyEnvelope: any;
    mediaKeySelfEnvelope?: any;
    origMime?: string;
  };
  api: { post: (url: string, data?: any, cfg?: any) => Promise<any> };
  token?: string | null;
  bundleKey?: string; 
}): Promise<string> {
  const fd = new FormData();
  fd.append("receiverId", toUserId);
  fd.append("media", file, file.name);
  fd.append("ttlAfterViewSec", String(ttlAfterViewSec));
  if (replyToId) fd.append("replyToId", replyToId);

  if (encryptedMedia) {
    fd.append("mediaIv", encryptedMedia.mediaIv);
    fd.append("mediaKeyEnvelope", JSON.stringify(encryptedMedia.mediaKeyEnvelope));
    if (encryptedMedia.mediaKeySelfEnvelope) {
      fd.append("mediaKeySelfEnvelope", JSON.stringify(encryptedMedia.mediaKeySelfEnvelope));
    }
    if (encryptedMedia.origMime) {
      fd.append("origMime", encryptedMedia.origMime);
    }
  }
  if (bundleKey) {
    fd.append("bundleKey", bundleKey);
  }
  const res = await api.post("/message/send", fd);
  return String(res.data?._id);
}


// Upload a batch (album) and optionally send an "album manifest" text
export async function sendMediaBatch(
  files: File[],
  {
    toUserId,
    ttlAfterViewSec,
    replyToId,
    makeAlbumManifest = true,
    api,
    token,
    bundleKey,
  }: {
    toUserId: string;
    ttlAfterViewSec: number;
    replyToId?: string;
    makeAlbumManifest?: boolean;
    api: { post: (url: string, data?: any, cfg?: any) => Promise<any> };
    token?: string | null;
    bundleKey?: string;
  }
): Promise<string[]> {
  const ids: string[] = [];
  
  const albumBundleKey = bundleKey || makeBundleKey();

  for (const f of files) {
    const id = await sendOneMedia({
      file: f,
      toUserId,
      ttlAfterViewSec,
      replyToId,
      api,
      token,
      bundleKey: albumBundleKey,  
      });
    ids.push(id);
  }

  if (makeAlbumManifest && ids.length > 1) {
    const manifest = JSON.stringify({ kind: "album", ids });
  
    const mySecret = await requireMySecret();
    const myPub    = publicKeyFromSecret(mySecret);
    const recipientPub = await requireRecipientKey(toUserId);
    const encrypted    = encryptMessage(manifest, recipientPub, mySecret);
    const selfEnvelope = encryptMessage(manifest, myPub,        mySecret);
  
    await api.post(
      "/message/send",
      { receiverId: toUserId, encrypted, selfEnvelope, bundleKey: albumBundleKey }
    );    
  }
  

  return ids;
}